/* @jsx h */
import { defineComponent, h } from "vue";
import {
  PaymentsSummaryReport,
  OrdersStatusReportDatum,
  OrdersBreakdown,
} from "@lemmonhq/types";
import { formatPriceInLocale } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { MobileSummaryCard } from "./MobileSummaryCard";

export const SummaryStats = defineComponent<{
  data: PaymentsSummaryReport;
  ordersBreakdown: OrdersBreakdown;
  currency: string;
  showExportLink?: boolean;
}>({
  name: "SummaryStats",
  props: ["data", "ordersBreakdown", "currency", "showExportLink"],
  setup(props) {
    const { t } = useLocalization();
    const statClass =
      "stat flex min-w-0 flex-col items-center justify-center rounded-lg border bg-base-200/60";
    const iconBadge =
      "material-symbols-outlined flex size-10 shrink-0 items-center justify-center rounded-full bg-base-100 text-2xl leading-none shadow-sm";

    return () => {
      const by_service = props.ordersBreakdown.by_service_method || [];
      const by_status = props.ordersBreakdown.by_status || [];
      const totalCount = by_service.reduce(
        (sum, el) => sum + el.total_count || 0,
        0,
      );
      const totalRevenue = by_service.reduce(
        (sum, el) => sum + el.total_revenue || 0,
        0,
      );
      const currency = props.currency;
      const canceledOrders: OrdersStatusReportDatum = by_status.find(
        (el) => el.status === "canceled",
      ) || {
        status: "canceled",
        total_revenue: 0,
        total_count: 0,
      };
      return (
        <>
          <MobileSummaryCard
            data={props.data}
            ordersBreakdown={props.ordersBreakdown}
            currency={currency}
            showExportLink={props.showExportLink}
          />

          <div class="hidden grid-cols-2 gap-4 md:grid lg:grid-cols-5">
            <div class={statClass}>
              <div class="stat-title flex gap-2 justify-center items-center text-prominent">
                <span class={[iconBadge, "text-primary"]}>account_balance</span>
                <span>{t("reports.dashboard.sales")}</span>
              </div>
              <div class="stat-value text-2xl md:text-3xl xl:text-4xl text-accent font-normal flex justify-center items-center">
                {formatPriceInLocale(
                  props.data.total_amount,
                  false,
                  2,
                  currency,
                )}
              </div>
              <div class="stat-desc flex justify-center items-center whitespace-normal text-center">
                <span class="material-symbols-outlined text-success text-sm">
                  trending_up
                </span>
                {props.data.total_count} {t("reports.dashboard.payments")}
              </div>
            </div>

            <div class={statClass}>
              <div class="stat-title flex gap-2 justify-center items-center text-prominent">
                <span class={[iconBadge, "text-success"]}>savings</span>
                <span>{t("reports.dashboard.tips")}</span>
              </div>
              <div class="stat-value text-2xl md:text-3xl xl:text-4xl text-accent font-normal flex justify-center items-center">
                {formatPriceInLocale(props.data.total_tips, false, 2, currency)}
              </div>
              <div class="stat-desc flex justify-center items-center whitespace-normal text-center">
                {t("reports.dashboard.avg")} {currency}{" "}
                {formatPriceInLocale(
                  props.data.total_tips / totalCount || 0,
                  false,
                  2,
                  currency,
                )}
                /{t("reports.dashboard.order")}
              </div>
            </div>

            <div class={statClass}>
              <div class="stat-title flex gap-2 justify-center items-center text-prominent">
                <span class={[iconBadge, "text-warning"]}>monitoring</span>
                <span>{t("reports.dashboard.discount")}</span>
              </div>
              <div class="stat-value text-2xl md:text-3xl xl:text-4xl text-accent font-normal flex justify-center items-center">
                {formatPriceInLocale(
                  props.data.discount_amount || 0,
                  false,
                  2,
                  currency,
                )}
              </div>
              <div class="stat-desc flex justify-center items-center whitespace-normal text-center">
                {props.data.discount_count} {t("reports.dashboard.discount")}
              </div>
            </div>

            <div class={statClass}>
              <div class="stat-title flex gap-2 justify-center items-center text-prominent">
                <span class={[iconBadge, "text-error"]}>delete</span>
                <span>{t("reports.dashboard.cancelled")}</span>
              </div>
              <div class="stat-value text-2xl md:text-3xl xl:text-4xl text-accent font-normal flex justify-center items-center">
                {formatPriceInLocale(
                  canceledOrders.total_revenue,
                  false,
                  2,
                  currency,
                )}
              </div>
              <div class="stat-desc flex justify-center items-center whitespace-normal text-center">
                {canceledOrders.total_count} {t("reports.dashboard.orders")}
              </div>
            </div>

            <div class={statClass}>
              <div class="stat-title flex gap-2 justify-center items-center text-prominent">
                <span class={[iconBadge, "text-info"]}>receipt_long</span>
                <span>{t("reports.dashboard.orders")}</span>
              </div>
              <div class="stat-value text-2xl md:text-3xl xl:text-4xl text-accent font-normal flex justify-center items-center">
                {totalCount}
              </div>
              <div class="stat-desc flex justify-center items-center whitespace-normal text-center">
                {formatPriceInLocale(
                  totalRevenue / totalCount || 0,
                  true,
                  2,
                  currency,
                )}{" "}
                Avg
              </div>
            </div>
          </div>
        </>
      );
    };
  },
});
